'use client';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import { ShowcaseCopyContext, translateShowcaseNode } from '../translation';
import {
  BuyerSlideHeading,
  BuyerSlideTable,
  od,
  md,
  pd,
  dd,
  fd,
} from './publish-approval';
import { zl, Vl, Wl, MarketReveal } from './collection-animation';
import { MarketImage, MarketPrompt, MarketFile } from './market-primitives';
import {
  Scene_gd,
  BuyerProductSlide,
  BuyerPriceSlide,
  BuyerTermsSlide,
  BuyerNextSteps,
} from './buyer-package-slides';
import { BlurSwap, CompletionMark } from './playback-controls';
import { collectionFacts, marketFacts } from './workflow-facts';
export function BuyerLineSheet({ lt: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <BuyerSlideHeading
      title={translateShowcaseNode(showcaseCopy, 'Line sheet')}
      slide={11}
    >
      <BuyerSlideTable
        lt={e}
        cols={'1.6em 0.9fr 1.9fr 1.3fr 0.9fr 0.9fr 0.9fr'}
        head={['', 'No.', 'Style', 'Colourway', 'Sizes', 'WSP', 'MSRP']}
        rows={[
          ...zl.map((e) => ({
            key: e.id,
            cells: [
              <MarketImage
                key={'img'}
                asset={e.asset}
                className={'block size-[1.6em]'}
              />,
              e.number,
              e.title,
              <span key={'sw'} className={'flex items-center gap-[0.4em]'}>
                <span
                  className={'size-[0.95em] shrink-0 rounded-full'}
                  style={{
                    background: e.hex,
                    boxShadow: `inset 0 0 0 1px ${od}`,
                  }}
                />
                {translateShowcaseNode(showcaseCopy, e.colorway)}
              </span>,
              e.category === 'Tailoring' ? '0\u201314' : 'XS\u2013XL',
              Vl(e.wholesale),
              Vl(e.retail),
            ],
          })),
          {
            key: 'total',
            total: !0,
            cells: ['', '', 'Total', '', '', Vl(md), Vl(pd)],
          },
        ]}
      />
    </BuyerSlideHeading>
  );
}
export const Cd = zl.length * dd + 200;
export const wd = (e) => Math.max(-1, Math.min(Cd, Math.floor(e / 15) * 15));
export const Td = [
  {
    key: 'story',
    name: 'Assortment story',
    ms: 1500,
    slide: () => 1,
    Page: React.memo(Scene_gd),
  },
  {
    key: 'pieces',
    name: 'One page per piece',
    ms: zl.length * dd + 300,
    slide: (e) => 2 + fd(e),
    Page: React.memo(BuyerProductSlide),
  },
  {
    key: 'price',
    name: 'Price architecture',
    ms: 1500,
    slide: () => 8,
    Page: React.memo(BuyerPriceSlide),
  },
  {
    key: 'terms',
    name: 'Proposed terms',
    ms: 1200,
    slide: () => 9,
    Page: React.memo(BuyerTermsSlide),
  },
  {
    key: 'next',
    name: 'Next step',
    ms: 1e3,
    slide: () => 10,
    Page: React.memo(BuyerNextSteps),
  },
  {
    key: 'sheet',
    name: 'Line sheet',
    ms: 1500,
    slide: () => 11,
    Page: React.memo(BuyerLineSheet),
  },
];
export const Ed = 500;
export const Dd = Td.reduce(
  (e, t, n) => [...e, (e[n - 1] ?? Ed) + (n ? (Td[n - 1]?.ms ?? 0) : 0)],
  []
);
export const Od = (e) => Dd[e] ?? Ed;
export const kd = Od(Td.length - 1) + (Td.at(-1)?.ms ?? 0) + 150;
export const Scene_jd = React.memo(function BuyerPackageThumbnail() {
  return (
    <span className={'mk-deck absolute inset-0 block'}>
      <Scene_gd lt={Cd} />
    </span>
  );
});
export function BuyerPackage({ t: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let t = Math.max(
      0,
      Td.reduce((t, n, r) => (e >= Od(r) ? r : t), 0)
    ),
    n = Td[t] ?? Td[0],
    r = n?.slide(e - Od(t)) ?? 1;
  return (
    <div className={'flex flex-col gap-4'}>
      <MarketPrompt
        t={e}
        workflow={'Create a Buyer Package'}
        brand={'lisiere'}
        prompt={`FW27 for ${Wl.audience}`}
        doneAt={kd}
        busy={'Building the buyer package'}
        result={'Buyer package ready'}
        meta={`${Wl.slides} slides · 2 files`}
      />
      <div className={'grid gap-3 lg:grid-cols-[minmax(0,1fr)_288px] lg:gap-4'}>
        <MarketReveal on={e >= 300} className={'mk-card p-2'}>
          <div
            className={
              'mk-deck mk-deck-main relative aspect-[16/10] overflow-hidden rounded-[8px] sm:aspect-[16/9]'
            }
          >
            {translateShowcaseNode(
              showcaseCopy,
              Td.map(({ key: n, Page: Component_r }, i) => (
                <div
                  key={n}
                  className={'mk-slide absolute inset-0'}
                  data-active={i === t}
                  aria-hidden={i !== t}
                >
                  <Component_r lt={wd(e - Od(i))} />
                </div>
              ))
            )}
            <span aria-hidden={!0} className={'mk-outline rounded-[8px]'} />
          </div>
          <div
            className={
              'text-ink-3 flex h-8 items-center justify-between px-1.5 pt-2 text-[12px]'
            }
          >
            <span className={'text-ink font-medium'}>
              <BlurSwap value={n?.name ?? ''} />
            </span>
            <span className={'num'}>
              {translateShowcaseNode(showcaseCopy, 'Slide ')}
              <BlurSwap value={String(r)} />
              {translateShowcaseNode(showcaseCopy, ' of ')}
              {translateShowcaseNode(showcaseCopy, Wl.slides)}
            </span>
          </div>
        </MarketReveal>
        <div className={'flex flex-col gap-3'}>
          <ol
            className={
              'mk-card flex flex-col p-1.5 max-lg:grid max-lg:grid-cols-6 max-lg:gap-1.5 lg:gap-0.5'
            }
          >
            {translateShowcaseNode(
              showcaseCopy,
              Td.map(({ key: n, name: r, Page: Component_i }, a) => {
                let o = e - Od(a);
                return (
                  <MarketReveal
                    key={n}
                    as={'li'}
                    on={o >= 0}
                    className={`flex items-center gap-2.5 rounded-[8px] p-1 transition-[background-color] duration-200 max-lg:flex-col max-lg:items-stretch max-lg:gap-1 ${a === t ? 'bg-[var(--state-wash)]' : ''}`}
                  >
                    <span
                      className={
                        'mk-deck relative block aspect-[16/9] w-full shrink-0 overflow-hidden rounded-[4px] lg:w-16'
                      }
                    >
                      <span className={'absolute inset-0 block'}>
                        <Component_i lt={wd(o)} />
                      </span>
                      <span
                        aria-hidden={!0}
                        className={'mk-outline rounded-[4px]'}
                      />
                    </span>
                    <span
                      className={
                        'text-ink min-w-0 flex-1 truncate text-[13px] max-lg:hidden'
                      }
                    >
                      {translateShowcaseNode(showcaseCopy, r)}
                    </span>
                    <CompletionMark
                      done={o >= (Td[a]?.ms ?? 1200) - 200}
                      className={'size-4 shrink-0 max-lg:hidden'}
                    />
                  </MarketReveal>
                );
              })
            )}
          </ol>
          <MarketFile
            on={e >= kd}
            name={'lisiere-fw27-buyers.pdf'}
            kind={`PDF · ${Wl.slides} slides`}
            icon={'doc'}
            thumb={<Scene_jd />}
          />
          <MarketFile
            on={e >= kd}
            delay={80}
            name={'lisiere-fw27-buyers.pptx'}
            kind={'Slides you can edit'}
            icon={'deck'}
            thumb={<Scene_jd />}
          />
        </div>
      </div>
    </div>
  );
}
export const Md = [
  ...(collectionFacts.stages.find((e) => e.key === 'sell')?.workflows ?? []),
  ...marketFacts.workflows,
];
export const Nd = (e) => Md.find((t) => t.name === e)?.does ?? '';
export const Pd = (e, t, n, r, i, a) => ({
  key: e,
  part: t,
  label: n,
  icon: r,
  workflow: i,
  does: Nd(i),
  ms: a,
});
export const Fd = [
  Pd(
    'channels',
    'sell',
    'Channel Assortment',
    'layers',
    'Create a Channel Assortment',
    8200
  ),
  Pd('buyer', 'sell', 'Buyer Package', 'deck', 'Create a Buyer Package', 9400),
  Pd('launch', 'market', 'Launch Plan', 'clock', 'Plan a Launch', 6400),
  Pd(
    'campaign',
    'market',
    'Campaign Kit',
    'megaphone',
    'Create a Campaign Kit',
    7400
  ),
  Pd(
    'listing',
    'market',
    'Listing Images',
    'image',
    'Create Product Listing Images',
    6200
  ),
  Pd(
    'page',
    'market',
    'Product Page',
    'store',
    'Build Product Page Content',
    7400
  ),
  Pd(
    'video',
    'market',
    'Product Video',
    'play',
    'Create a Product Video',
    7800
  ),
  Pd(
    'email',
    'market',
    'Launch Email',
    'mail',
    'Create a Marketing Email',
    7400
  ),
  {
    key: 'publish',
    part: 'market',
    label: 'Approve',
    icon: 'check',
    workflow: null,
    does: 'Nothing goes out until your team approves it.',
    ms: 6400,
  },
];
export const Id = [
  {
    value: 'sell',
    label: 'Sell',
    brand: 'LISI\xC8RE',
  },
  {
    value: 'market',
    label: 'Market',
    brand: 'Maison Aubrac',
  },
];
